import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, CheckCircle2, XCircle, Stethoscope, Clock } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (new Date() - new Date(iso)) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function ConfirmModal({ req, open, onClose, onDone }) {
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const [wardId, setWardId] = useState("");
  const [bedId, setBedId] = useState("");
  const [override, setOverride] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/ipd/wards").then((r) => setWards(r.data)).catch(() => {});
    setBedId(""); setOverride(""); setWardId("");
  }, [open]);

  useEffect(() => {
    if (!wardId) { setBeds([]); return; }
    api.get(`/ipd/beds?ward_id=${wardId}&status=available`)
       .then((r) => setBeds(r.data)).catch(() => {});
  }, [wardId]);

  const submit = async () => {
    if (!wardId) { toast.error("Select a ward"); return; }
    setSaving(true);
    try {
      await api.put(`/ipd/admissions/request/${req.id}/confirm`, {
        ward_id: wardId,
        bed_id: bedId || null,
        reason_override: override || null,
      });
      toast.success("Admission confirmed — bed allocated");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally { setSaving(false); }
  };

  if (!req) return null;
  const v = req.vitals_on_admission || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="pending-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Confirm &amp; Allocate Bed
          </DialogTitle>
        </DialogHeader>

        {/* OPD data — read-only */}
        <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded p-3 border">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Patient</p>
            <p className="font-semibold">{req.patient_name}</p>
            <p className="text-xs text-slate-600">{req.patient_age} · {req.patient_gender}</p>
            <p className="text-xs text-slate-600">{req.patient_mobile}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Referring Doctor</p>
            <p className="font-semibold">{req.doctor_name}</p>
            <p className="text-xs text-slate-600">NMC {req.doctor_nmc || "—"}</p>
          </div>
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Chief Complaint</p>
            <p>{req.chief_complaint || "—"}</p>
          </div>
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Medical History</p>
            <p className="text-xs">{req.past_medical_history || "—"}</p>
          </div>
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Current Medications</p>
            <p className="text-xs">
              {(req.current_medications || []).join(", ") || "—"}
            </p>
          </div>
          <div className="col-span-2 grid grid-cols-4 gap-2 pt-2">
            <div><p className="text-[10px] uppercase text-slate-500">Temp</p><p>{v.temperature ?? "—"}°C</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">BP</p><p>{v.blood_pressure ?? "—"}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">HR</p><p>{v.heart_rate ?? "—"}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">RR</p><p>{v.respiratory_rate ?? "—"}</p></div>
          </div>
        </div>

        {/* Receptionist fills */}
        <div className="grid gap-3 pt-2">
          <div><Label>Ward *</Label>
            <Select value={wardId} onValueChange={setWardId}>
              <SelectTrigger data-testid="pending-confirm-ward"><SelectValue placeholder="Select ward" /></SelectTrigger>
              <SelectContent>
                {wards.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.ward_name} · {w.available_beds}/{w.total_beds} free
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Available bed</Label>
            <Select value={bedId} onValueChange={setBedId} disabled={!wardId}>
              <SelectTrigger data-testid="pending-confirm-bed">
                <SelectValue placeholder={wardId ? "Auto-assign or pick one" : "Pick a ward first"} />
              </SelectTrigger>
              <SelectContent>
                {beds.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.bed_number} · {b.bed_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Reason override (optional)</Label>
            <Textarea value={override} onChange={(e) => setOverride(e.target.value)}
                      placeholder={req.reason_for_admission} maxLength={500} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="pending-confirm-submit">
            <CheckCircle2 size={14} className="mr-1" /> {saving ? "Confirming…" : "Confirm & Admit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectModal({ req, open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 2) { toast.error("Reason too short"); return; }
    setSaving(true);
    try {
      await api.delete(`/ipd/admissions/request/${req.id}`, { data: { reason } });
      toast.success("Rejected — doctor will be notified");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="pending-reject-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <XCircle className="w-5 h-5" /> Reject Admission Request
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Reason *</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. No beds available in requested ward"
                    data-testid="pending-reject-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} variant="destructive"
                  data-testid="pending-reject-submit">
            {saving ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PendingAdmissionsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [confirmReq, setConfirmReq] = useState(null);
  const [rejectReq, setRejectReq] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ipd/admissions/pending");
      setRows(data);
    } catch { /* silent — panel just stays empty */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Poll for new pending admissions every 30 s so receptionists get
    // fresh doctor referrals without needing to refresh.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const doneAndRefresh = () => { load(); onChanged?.(); };

  if (loading || rows.length === 0) return null;

  return (
    <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="pending-admissions-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Bell className="w-5 h-5 text-amber-700" />
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold
                           rounded-full w-4 h-4 flex items-center justify-center"
                data-testid="pending-count-badge">
            {rows.length}
          </span>
        </div>
        <p className="font-heading font-semibold text-slate-900">
          {rows.length} Pending Admission{rows.length > 1 ? "s" : ""}
        </p>
        <span className="text-xs text-slate-500">from doctors</span>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded border border-slate-200 p-3 flex items-start justify-between gap-3"
               data-testid={`pending-row-${r.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-900">{r.patient_name}</p>
                <Badge variant="outline" className="text-[10px]">
                  {r.patient_age} · {r.patient_gender}
                </Badge>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock size={11} /> {timeAgo(r.created_at)}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                <Stethoscope size={11} className="inline mr-1" />
                {r.doctor_name}  ·  {r.reason_for_admission}
              </p>
              {r.chief_complaint && (
                <p className="text-xs text-slate-500 mt-1 italic">CC: {r.chief_complaint}</p>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={() => setConfirmReq(r)}
                      data-testid={`pending-confirm-${r.id}`}>
                <CheckCircle2 size={14} className="mr-1" /> Confirm
              </Button>
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                      onClick={() => setRejectReq(r)}
                      data-testid={`pending-reject-${r.id}`}>
                <XCircle size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmModal req={confirmReq} open={!!confirmReq}
                    onClose={() => setConfirmReq(null)} onDone={doneAndRefresh} />
      <RejectModal req={rejectReq} open={!!rejectReq}
                   onClose={() => setRejectReq(null)} onDone={doneAndRefresh} />
    </Card>
  );
}
